import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { localizeSchema } from '../utils/templateText'
import {
  fetchGenerationResult,
  fetchGenerationStatus,
  fetchRemoteImage,
  generateAdWithAi,
  loadAd,
  loadTemplate,
  listTemplates,
  publishAd,
  saveAd,
} from '../services/api'
import { adaptTemplateBundle } from '../schemas/schemaAdapters'
import { newPendingAssetId, newPendingRenditionId, previewConfiguration, validateBrowserAsset } from '../utils/assets'
import { normalizeConditionalOptions } from '../utils/conditions'
import { hyphenateConfiguration, loadHyphenator, stripSoftHyphens } from '../utils/hyphenation'
import { cloneValue, getAtPath, setAtPath } from '../utils/configPaths'
import {
  cropAroundFocus,
  cropForFocus,
  cropForTarget,
  createCropPreview,
  emptyMediaReference,
  readImageDimensions,
  targetForProfile,
} from '../utils/imageCrops'
import { migrateConfiguration } from '../utils/migrations'
import { serverMessage } from '../utils/serverMessage'
import { applyGenerationResult } from '../utils/generationResult'
import { importGenerationAssets } from '../utils/generationAssets'
import { advertiserChanged } from '../utils/advertiserSwitch'
import { carriesContent, carryTemplateContent } from '../utils/templateCarry'
import { withKeptLibrary, withLibraryImage } from '../utils/aiSuggestions'
import { useEditHistory } from './useEditHistory'
import {
  loadTemplateSnapshots,
  persistTemplateSnapshots,
  referencedAssetIds,
  remapSnapshot,
  savedAssetMapping,
  takeSnapshot,
  withoutLostMedia,
} from '../utils/templateSnapshots'
import { validateConfiguration } from '../utils/validation'
import {
  applyEditorFieldValue,
  assistantImageField,
  editorCollectionDescriptor,
  itemIndexById,
} from '../utils/assistantOperations'

// One hook dispatches historical fixed-slide and Package V2 generic collection contracts.
import {
  clearImageSelections,
  collectionById,
  imageFieldReferences,
  newCollectionIdentity,
  withAddedItem,
  withDuplicatedItem,
  withMovedItem,
  withoutItem,
} from '../utils/schemaV2'

const DEFAULT_TEMPLATE_FAMILY = 'split-slider'
const AD_V4 = 'studio-editor-v2/ad-v4'
const TEXT_FIELD_TYPES = new Set(['text', 'textarea', 'rich-text', 'url'])

// Campaign Setting metadata (editor-only, like `suggestions`): the advertiser the ad is for,
// the clickout mode and the formats the campaign books. New ads book the format they are
// created in (AD-253; every format without one) and click out to the advertiser's own website.
export function defaultCampaign(manifest, formatId = null) {
  const formats = (manifest?.formats || []).map((format) => format.id)
  return {
    advertiser: null,
    clickoutMode: 'own-website',
    formats: formats.includes(formatId) ? [formatId] : formats,
  }
}

function campaignFor(configuration, manifest) {
  const fallback = defaultCampaign(manifest)
  const current = configuration?.campaign
  if (!current || typeof current !== 'object') return fallback
  const known = new Set(fallback.formats)
  const formats = (Array.isArray(current.formats) ? current.formats : []).filter((id) => known.has(id))
  return {
    advertiser: current.advertiser || null,
    // AD-234: an older draft without a recorded clickout opens with Own Website.
    clickoutMode: current.clickoutMode === 'landing-page' ? 'landing-page' : 'own-website',
    formats: formats.length ? formats : fallback.formats,
  }
}

function withoutCampaign(configuration) {
  if (!configuration || !Object.prototype.hasOwnProperty.call(configuration, 'campaign')) return configuration
  const { campaign, ...rest } = configuration // eslint-disable-line no-unused-vars
  return rest
}

function normalizedWebsite(value) {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return null
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`)
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null
  } catch {
    return null
  }
}

function schemaField(schema, path) {
  for (const section of schema?.sections || []) {
    const field = (section.fields || []).find((entry) => entry.path === path)
    if (field) return field
  }
  return null
}

function formatKey(configuration, manifest) {
  if (!configuration?.format?.id) return null
  return [
    configuration.format.id,
    configuration.format.width,
    configuration.format.height,
    ...Object.values(manifest?.assetProfiles || {}).flatMap((profile) => (
      profile.target?.type === 'matrix'
        ? profile.target.selectors.map((path) => getAtPath(configuration, path)) : []
    )),
  ].join(':')
}

// The generation proxy forwards the agent's body as `details`; without it the alert only
// says "submit failed" and the real reason (a 422 field, a missing schema) is lost.
function upstreamDetails(details) {
  if (!details) return null
  if (typeof details === 'string') return details
  if (Array.isArray(details.detail)) {
    return details.detail
      .map((entry) => {
        const location = Array.isArray(entry?.loc)
          ? entry.loc.filter((part) => part !== 'body').join('.')
          : ''
        const message = entry?.msg || ''
        return location ? `${location}: ${message}` : message
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof details.detail === 'string') return details.detail
  return details.error || details.message || null
}

function serverErrors(error) {
  const response = error?.response
  if (response?.status === 409) {
    return {
      fields: {},
      form: response?.data?.message
        || i18n.t('editorV2.core.errors.conflict'),
      conflict: true,
      code: response?.data?.code || null,
    }
  }

  const errors = response?.data?.errors || {}
  const normalized = Object.fromEntries(
    Object.entries(errors).map(([path, messages]) => [
      path,
      Array.isArray(messages) ? messages[0] : String(messages),
    ]),
  )
  // A rejected field the form does not show (suggestions, campaign, assets) is otherwise
  // reported only as Laravel's generic "The given data was invalid.".
  const detail = upstreamDetails(response?.data?.details) || Object.values(normalized)[0] || null
  const message = serverMessage(response?.data?.message) || error?.message
    || i18n.t('editorV2.core.errors.requestFailed')
  return {
    fields: normalized,
    form: detail && detail !== message ? `${message} ${detail}` : message,
    conflict: false,
    code: response?.data?.code || null,
  }
}

function sourceValue(value) {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized ? normalized : null
}

function generationSources(configuration) {
  return {
    briefing_text: sourceValue(configuration?.briefing_text),
    website: sourceValue(configuration?.website),
    social_post_url: sourceValue(configuration?.social_post_url),
    pdf_url: sourceValue(configuration?.pdf_url),
    creative_image_url: sourceValue(configuration?.creative_image_url),
    briefing_image_url: sourceValue(configuration?.briefing_image_url),
  }
}

const GENERATION_STEP_LABELS = {
  source_intelligence: 'editorV2.core.steps.sourceIntelligence',
  asset_assessment: 'editorV2.core.steps.assetAssessment',
  briefing_context: 'editorV2.core.steps.briefingContext',
  finalize_briefing: 'editorV2.core.steps.finalizeBriefing',
  slide_planning: 'editorV2.core.steps.slidePlanning',
  brand_colors: 'editorV2.core.steps.brandColors',
  creative_generation: 'editorV2.core.steps.creativeGeneration',
  text_removal: 'editorV2.core.steps.textRemoval',
  finalize_slide_plan: 'editorV2.core.steps.finalizeSlidePlan',
  slogan_matching: 'editorV2.core.steps.sloganMatching',
  focal_points: 'editorV2.core.steps.focalPoints',
  layout_selection: 'editorV2.core.steps.layoutSelection',
  ad_configuration: 'editorV2.core.steps.adConfiguration',
}

function normalizeGenerationStep(step) {
  if (!step) return null
  return String(step).trim().toLowerCase()
}

function generationStepLabel(step) {
  const normalized = normalizeGenerationStep(step)
  if (!normalized) return null
  const key = GENERATION_STEP_LABELS[normalized]
  return key ? i18n.t(key) : normalized.replaceAll('_', ' ')
}

function advanceGenerationSteps(existingSteps, currentStep, status) {
  const normalizedCurrentStep = normalizeGenerationStep(currentStep)
  const steps = Array.isArray(existingSteps) ? [...existingSteps] : []

  if (!normalizedCurrentStep || ['queued', 'starting', 'completed', 'failed'].includes(normalizedCurrentStep)) {
    if (status === 'completed' && steps.length > 0) {
      return steps.map((step, index) => ({
        ...step,
        state: index === (steps.length - 1) ? 'completed' : step.state,
      }))
    }
    return steps
  }

  const existingIndex = steps.findIndex((step) => step.key === normalizedCurrentStep)

  if (existingIndex === -1) {
    if (steps.length > 0) {
      const lastIndex = steps.length - 1
      steps[lastIndex] = { ...steps[lastIndex], state: 'completed' }
    }
    steps.push({
      key: normalizedCurrentStep,
      label: generationStepLabel(normalizedCurrentStep),
      state: status === 'completed' ? 'completed' : 'active',
    })
  } else {
    for (let index = 0; index < existingIndex; index += 1) {
      steps[index] = { ...steps[index], state: 'completed' }
    }
    steps[existingIndex] = {
      ...steps[existingIndex],
      state: status === 'completed' ? 'completed' : 'active',
      label: generationStepLabel(normalizedCurrentStep),
    }
  }

  return steps
}

/** Whether the template's language field offers `locale` (en-US, de-DE). */
function languageOffered(schema, locale) {
  const sections = [...(schema?.sections || []), ...((schema?.ui?.groups) || [])]
  for (const section of sections) {
    const field = (section.fields || []).find((entry) => entry.path === 'ad.language')
    if (field) return (field.options || []).some((option) => option.value === locale)
  }
  return false
}

// `designStart`: a "Let Us Design It" ad's briefing, which its first design starts from.
// `specStart`: a Leadbuster Spec Ad (AD-254), in its language; its key goes with the first save.
export function useStudioEditor({ adId: initialAdId, apiBase, initialTemplate = null, initialFormat = null, designStart = null, specStart = null }) {
  const [adId, setAdId] = useState(initialAdId || null)
  const [revision, setRevision] = useState(null)
  const [templates, setTemplates] = useState([])
  const [template, setTemplate] = useState(null)
  const { i18n: { language } } = useTranslation()
  const [configuration, setConfiguration] = useState(null)
  const [savedConfiguration, setSavedConfiguration] = useState(null)
  const [assetUrls, setAssetUrls] = useState({})
  const [assetSourceUrls, setAssetSourceUrls] = useState({})
  const [assetSrcSets, setAssetSrcSets] = useState({})
  const [pendingFiles, setPendingFiles] = useState({})
  const [selectedSlide, setSelectedSlide] = useState(0)
  const [selectedItems, setSelectedItems] = useState({})
  const [status, setStatus] = useState('loading')
  const [fieldErrors, setFieldErrors] = useState({})
  const [formError, setFormError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewKey, setPreviewKey] = useState(null)
  const [compatibility, setCompatibility] = useState(null)
  const [publication, setPublication] = useState(null)
  const [upgradeBackup, setUpgradeBackup] = useState(null)
  const [editorVersion, setEditorVersion] = useState(0)
  const [assistantUndo, setAssistantUndo] = useState(null)
  const [hyphenator, setHyphenator] = useState(null)
  const [generationJob, setGenerationJob] = useState(null)
  // A generation request refused before its job existed (e.g. 422): Step 1 shows why.
  const [generationRefusal, setGenerationRefusal] = useState(null)
  const configurationRef = useRef(null)
  const pendingFilesRef = useRef(pendingFiles)
  pendingFilesRef.current = pendingFiles
  const generationAbortRef = useRef(null)
  // The advertiser the pictures currently in the ad were gathered for. Empty
  // after a reload, where the saved brand name stands in for it.
  const generatedForRef = useRef(null)
  // Pending uploads and imports (asset id → { sourceUrl }) that join the ad's library once placed.
  const libraryUploadsRef = useRef({})
  // The ad as it was left in each template (family → snapshot), restored when switching back.
  const templateSnapshotsRef = useRef({})
  // A saved ad remembers, per release, which advertiser its pictures were gathered for. The key
  // name is older than this use; existing entries still carry the advertiser.
  const advertiserStorageKey = `studio-editor-v2:design-locks:${adId || 'new'}:${template?.manifest?.releaseKey || ''}`
  const advertiserStorageRef = useRef(null)
  useEffect(() => {
    if (!template || !configuration) return
    if (advertiserStorageRef.current === advertiserStorageKey) return
    try {
      const saved = adId ? JSON.parse(window.localStorage.getItem(advertiserStorageKey) || 'null') : null
      if (saved) generatedForRef.current = saved.advertiser || generatedForRef.current || null
    } catch { /* Storage is optional. */ }
    advertiserStorageRef.current = advertiserStorageKey
  }, [adId, advertiserStorageKey, template, configuration])
  // Written only when the advertiser changes, not on every keystroke.
  const lastAdvertiserWriteRef = useRef(null)
  useEffect(() => {
    if (!configuration || !template || !adId) return
    const payload = JSON.stringify({ advertiser: generatedForRef.current })
    if (lastAdvertiserWriteRef.current === advertiserStorageKey + payload) return
    try {
      window.localStorage.setItem(advertiserStorageKey, payload)
      lastAdvertiserWriteRef.current = advertiserStorageKey + payload
    } catch { /* Private browsing can disable storage. */ }
  }, [adId, configuration, advertiserStorageKey, template])
  const editorVersionRef = useRef(0)
  const previousFormatKeyRef = useRef(null)
  const formatRefreshRunRef = useRef(0)
  configurationRef.current = configuration

  // AD-234: puts back one Undo/Redo step. Picture previews are only ever added, never taken back.
  const restoreHistoryEntry = useCallback((entry, knownUrls) => {
    setAssistantUndo(null)
    setAssetUrls((current) => ({ ...knownUrls.assetUrls, ...current }))
    setAssetSourceUrls((current) => ({ ...knownUrls.assetSourceUrls, ...current }))
    setAssetSrcSets((current) => ({ ...knownUrls.assetSrcSets, ...current }))
    // A save hands back a new bundle object of the same release; only another release switches.
    const releaseOf = (bundle) => bundle?.manifest?.releaseKey || bundle?.manifest?.id
    if (releaseOf(entry.template) !== releaseOf(template)) {
      setTemplate(entry.template)
      setPreviewUrl(entry.template.previewUrl || null)
      setPreviewKey(entry.template.previewKey || entry.template.manifest.releaseKey || entry.template.manifest.id)
    }
    setConfiguration(cloneValue(entry.configuration))
    setSelectedSlide(entry.selectedSlide || 0)
    setSelectedItems({ ...(entry.selectedItems || {}) })
    setFieldErrors({})
    if (status !== 'conflict') {
      setFormError(null)
      setStatus('idle')
    }
  }, [status, template])
  const history = useEditHistory({
    template, configuration, assetUrls, assetSourceUrls, assetSrcSets, selectedSlide, selectedItems,
    apply: restoreHistoryEntry,
  })
  const {
    undo, redo, canUndo, canRedo,
    reset: resetHistory, amend: amendHistory, remap: remapHistory, referencedIds: historyAssetIds,
  } = history

  useEffect(() => {
    if (!configuration) return
    editorVersionRef.current += 1
    setEditorVersion(editorVersionRef.current)
  }, [configuration])

  // The saved copy changes only on load, save and resets: serialised once per change, not per keystroke.
  const savedJson = useMemo(() => (savedConfiguration ? JSON.stringify(savedConfiguration) : null), [savedConfiguration])
  const dirty = useMemo(() => {
    if (!configuration || savedJson === null) return false
    return JSON.stringify(configuration) !== savedJson
  }, [configuration, savedJson])

  useEffect(() => {
    let cancelled = false

    async function initialize() {
      setStatus('loading')
      setFormError(null)
      try {
        const available = await listTemplates(apiBase)
        if (cancelled) return
        setTemplates(available)

        let savedAd = null
        let templateReference = null
        if (initialAdId) {
          savedAd = await loadAd(apiBase, initialAdId)
          templateReference = savedAd.templateRelease?.releaseKey
            || savedAd.configuration.template.releaseKey
            || savedAd.configuration.template.id
        } else {
          if (!available.length) {
            throw new Error(i18n.t('editorV2.core.errors.noTemplates'))
          }
          const requested = available.find((entry) => (
            (entry.releaseKey || entry.id) === initialTemplate || entry.family === initialTemplate
          ))
          const preferred = requested || available.find(
            (entry) => entry.family === DEFAULT_TEMPLATE_FAMILY,
          ) || available[0]
          templateReference = preferred.releaseKey || preferred.id
        }
        const loadedTemplate = adaptTemplateBundle(savedAd?.templateBundle
          || await loadTemplate(apiBase, templateReference)
        )
        if (cancelled) return

        // Saved revisions carry soft hyphens for the runtime; the form works on clean text.
        const nextConfiguration = stripSoftHyphens(cloneValue(savedAd?.configuration || loadedTemplate.defaults))
        const requestedFormat = !savedAd && loadedTemplate.manifest.formats.find((entry) => entry.id === initialFormat)
        if (requestedFormat) {
          Object.keys(nextConfiguration.format).forEach((key) => {
            if (requestedFormat[key] !== undefined) nextConfiguration.format[key] = requestedFormat[key]
          })
        }
        if (!savedAd && nextConfiguration.schemaVersion === AD_V4) {
          nextConfiguration.campaign = defaultCampaign(loadedTemplate.manifest, nextConfiguration.format?.id)
        }
        // The design is named after the briefing's campaign, not the template's placeholder.
        if (!savedAd && designStart?.campaignName && nextConfiguration.ad) {
          nextConfiguration.ad.campaignName = designStart.campaignName
        }
        // The design is in the briefing's language, where the template offers it.
        const startLanguage = (designStart || specStart)?.language
        if (!savedAd && startLanguage && nextConfiguration.ad
          && languageOffered(loadedTemplate.schema, startLanguage)) {
          nextConfiguration.ad.language = startLanguage
        }
        // The advertiser recorded in Campaign Setting is who the ad's pictures belong to.
        const savedAdvertiser = nextConfiguration.campaign?.advertiser
        if (savedAdvertiser?.name) {
          generatedForRef.current = { name: savedAdvertiser.name, placeId: savedAdvertiser.placeId || null }
        }
        templateSnapshotsRef.current = savedAd ? loadTemplateSnapshots(nextConfiguration.id) : {}
        setTemplate(loadedTemplate)
        setConfiguration(nextConfiguration)
        setSavedConfiguration(cloneValue(nextConfiguration))
        setRevision(savedAd?.revision ?? null)
        setAssetUrls(savedAd?.assetUrls || {})
        setAssetSourceUrls(savedAd?.assetSourceUrls || savedAd?.assetUrls || {})
        setAssetSrcSets(savedAd?.assetSrcSets || {})
        setSavedAt(savedAd?.savedAt || null)
        setPreviewUrl(savedAd?.previewUrl || loadedTemplate.previewUrl || null)
        setPreviewKey(
          savedAd?.previewKey
          || loadedTemplate.previewKey
          || loadedTemplate.manifest.releaseKey
          || loadedTemplate.manifest.id,
        )
        setCompatibility(savedAd?.compatibility || {
          status: 'supported',
          documentVersion: 2,
          currentDocumentVersion: 2,
          legacyDocument: false,
          templateSource: 'central',
          upgradeOptions: [],
        })
        setPublication(savedAd?.publication || null)
        setSelectedItems(Object.fromEntries(
          (loadedTemplate.schema.collections || []).map((collection) => [collection.id, 0]),
        ))
        setStatus('idle')
      } catch (error) {
        if (!cancelled) {
          setFormError(serverErrors(error).form)
          setStatus('error')
        }
      }
    }

    initialize()
    return () => { cancelled = true }
  }, [apiBase, initialAdId, initialTemplate, initialFormat]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function warnOnUnload(event) {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnOnUnload)
    return () => window.removeEventListener('beforeunload', warnOnUnload)
  }, [dirty])

  useEffect(() => {
    if (dirty && ['saved', 'published'].includes(status)) setStatus('idle')
  }, [dirty, status])

  const currentFormatKey = formatKey(configuration, template?.manifest)
  const adLanguage = configuration?.ad?.language || null

  // Hyphenation patterns (German / English) load lazily per ad language; until they are
  // ready the preview shows the plain text.
  useEffect(() => {
    let cancelled = false
    loadHyphenator(adLanguage)
      .then((loaded) => { if (!cancelled) setHyphenator(loaded) })
      .catch(() => { if (!cancelled) setHyphenator(null) })
    return () => { cancelled = true }
  }, [adLanguage])

  useEffect(() => {
    if (
      !configuration
      || !template
      || !['studio-editor-v2/ad-v3', 'studio-editor-v2/ad-v4'].includes(configuration.schemaVersion)
    ) {
      return undefined
    }
    if (previousFormatKeyRef.current === null) {
      previousFormatKeyRef.current = currentFormatKey
      return undefined
    }
    if (!currentFormatKey || previousFormatKeyRef.current === currentFormatKey) {
      return undefined
    }

    const snapshot = configurationRef.current
    // Both `mediaReferences` and `v3CropReferences` were referenced here but neither was
    // defined or imported, so re-cropping on a format change threw a ReferenceError and
    // unmounted the editor for every template. Image fields are declared identically in
    // schema-v1 and schema-v2, so one dialect-agnostic walk serves ad-v3 and ad-v4 alike.
    const references = imageFieldReferences(template.schema, snapshot)
      .map((reference) => ({
        ...reference,
        media: getAtPath(snapshot, reference.path),
      }))
      .filter((reference) => reference.media?.sourceAssetId)
    const sourcesReady = references.every(
      ({ media }) => assetSourceUrls[media.sourceAssetId] || snapshot.assets?.[media.sourceAssetId]?.url,
    )
    if (!sourcesReady) return undefined
    if (!references.length) {
      previousFormatKeyRef.current = currentFormatKey
      return undefined
    }

    const run = ++formatRefreshRunRef.current
    let cancelled = false
    const refresh = async () => {
      const updates = []
      const failures = {}
      const processed = await Promise.all(references.map(async (reference) => {
        try {
          const sourceAssetId = reference.media.sourceAssetId
          const sourceUrl = assetSourceUrls[sourceAssetId] || snapshot.assets?.[sourceAssetId]?.url
          const target = targetForProfile(
            template.manifest,
            reference.profile,
            snapshot,
          )
          if (!target || target.mode !== 'crop') return null
          const rendered = await createCropPreview(
            sourceUrl,
            reference.media.crop,
            target,
            reference.media.focalPoint,
          )
          const renditionAssetId = newPendingRenditionId()
          return {
            type: 'update',
            path: reference.path,
            sourceAssetId,
            previousCrop: JSON.stringify(reference.media.crop || null),
            renditionAssetId,
            crop: rendered.crop,
            previewUrl: rendered.previewUrl,
          }
        } catch {
          return {
            type: 'failure',
            path: reference.path,
            message: i18n.t('editorV2.core.errors.autoResizeFailed'),
          }
        }
      }))
      processed.forEach((result) => {
        if (!result) return
        if (result.type === 'update') updates.push(result)
        if (result.type === 'failure') failures[result.path] = result.message
      })

      if (
        cancelled
        || formatRefreshRunRef.current !== run
        || formatKey(configurationRef.current, template?.manifest) !== currentFormatKey
      ) {
        return
      }
      previousFormatKeyRef.current = currentFormatKey
      if (updates.length) {
        setAssetUrls((current) => ({
          ...current,
          ...Object.fromEntries(updates.map((entry) => [
            entry.renditionAssetId,
            entry.previewUrl,
          ])),
        }))
        setConfiguration((current) => {
          if (!current || formatKey(current, template?.manifest) !== currentFormatKey) return current
          let next = current
          updates.forEach((entry) => {
            const media = getAtPath(next, entry.path)
            if (
              media?.sourceAssetId !== entry.sourceAssetId
              || JSON.stringify(media.crop || null) !== entry.previousCrop
            ) {
              return
            }
            next = setAtPath(next, entry.path, {
              ...media,
              renditionAssetId: entry.renditionAssetId,
              crop: entry.crop,
            })
          })
          // Derived from the format change, not an edit of its own (Undo/Redo).
          if (next !== current) amendHistory()
          return next
        })
      }
      setFieldErrors((current) => {
        const next = { ...current }
        references.forEach(({ path }) => delete next[path])
        return { ...next, ...failures }
      })
    }

    refresh()
    return () => {
      cancelled = true
    }
  }, [assetSourceUrls, currentFormatKey, configuration?.schemaVersion, template])

  const updateValue = useCallback((path, value) => {
    setAssistantUndo(null)
    const libraryUpload = value?.sourceAssetId ? libraryUploadsRef.current[value.sourceAssetId] : null
    setConfiguration((current) => {
      let next = applyEditorFieldValue(current, path, value, template)
      if (libraryUpload) {
        next = withLibraryImage(next, value.sourceAssetId, {
          logos: /(^|\.)brand\.(logo|favicon)$/.test(path), sourceUrl: libraryUpload.sourceUrl,
        })
      }
      return normalizeConditionalOptions(template?.schema, next)
    })
    setFieldErrors((current) => {
      if (!current[path]) return current
      const next = { ...current }
      delete next[path]
      return next
    })
    if (status !== 'conflict') {
      setFormError(null)
      setStatus('idle')
    }
  }, [status, template])

  const setSelectedItem = useCallback((collectionId, index) => {
    setSelectedItems((current) => ({ ...current, [collectionId]: Math.max(0, Number(index || 0)) }))
  }, [])

  // `change` returns the collection's next items and the index to select, or null to leave it as it is.
  const updateCollection = useCallback((collectionId, change) => {
    setAssistantUndo(null)
    const collection = collectionById(template?.schema, collectionId)
    if (!collection) return
    setConfiguration((current) => {
      const next = change(getAtPath(current, collection.path, []), collection)
      if (!next) return current
      setSelectedItem(collectionId, next.selected)
      return setAtPath(current, collection.path, next.items)
    })
  }, [setSelectedItem, template])

  const addCollectionItem = useCallback((collectionId) => {
    updateCollection(collectionId, withAddedItem)
  }, [updateCollection])

  const removeCollectionItem = useCallback((collectionId, index) => {
    updateCollection(collectionId, (items, collection) => withoutItem(items, collection, index))
  }, [updateCollection])

  const moveCollectionItem = useCallback((collectionId, index, direction) => {
    updateCollection(collectionId, (items) => withMovedItem(items, index, index + direction))
  }, [updateCollection])

  // Drag-and-drop reorder (slide tabs): moves one item to any position.
  const reorderCollectionItem = useCallback((collectionId, from, to) => {
    updateCollection(collectionId, (items) => withMovedItem(items, from, to))
  }, [updateCollection])

  const duplicateCollectionItem = useCallback((collectionId, index) => {
    updateCollection(collectionId, (items, collection) => withDuplicatedItem(items, collection, index))
  }, [updateCollection])

  const addAsset = useCallback((file, options = {}) => {
    const error = validateBrowserAsset(file, options.allowedTypes || null, options.maxBytes || null)
    if (error) {
      setFormError(error)
      return null
    }

    const assetId = newPendingAssetId()
    setPendingFiles((current) => ({ ...current, [assetId]: file }))
    if (options.library) libraryUploadsRef.current[assetId] = options.library
    // Large or undecodable clips go to the server as they are; reading them into a data URL
    // would only fill memory and the preview message.
    if (options.uploadOnly) {
      setFormError(null)
      return assetId
    }
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      if (!options.sourceOnly) {
        setAssetUrls((current) => ({ ...current, [assetId]: reader.result }))
      }
      setAssetSourceUrls((current) => ({ ...current, [assetId]: reader.result }))
    })
    reader.addEventListener('error', () => {
      setFormError(i18n.t('editorV2.core.errors.filePreviewUnreadable'))
    })
    reader.readAsDataURL(file)
    setFormError(null)
    return assetId
  }, [])

  const addRenditionPreview = useCallback((previewUrl) => {
    const assetId = newPendingRenditionId()
    setAssetUrls((current) => ({ ...current, [assetId]: previewUrl }))
    return assetId
  }, [])

  const addSlide = useCallback(() => {
    setAssistantUndo(null)
    setConfiguration((current) => {
      if (!current || current.slides.length >= 6) return current
      const seed = cloneValue(template?.defaults?.slides?.[0] || {})
      seed.id = newCollectionIdentity('slide')
      seed.kicker = ''
      // Ad content, so in the ad's language, not the interface's (AD-256).
      seed.headline = i18n.t('editorV2.core.defaults.newSlideHeadline', {
        lng: String(current.ad?.language || 'en').slice(0, 2),
      })
      seed.subheadline = ''
      seed.bullets = []
      seed.information = ''
      seed.contactRows = []
      if (Object.prototype.hasOwnProperty.call(seed, 'media')) {
        seed.media = emptyMediaReference()
      } else {
        seed.imageAssetId = null
      }
      seed.countdown = { enabled: false, mode: 'text', endAt: null }
      const slides = [...current.slides, seed]
      setSelectedSlide(slides.length - 1)
      return { ...current, slides }
    })
  }, [template])

  const removeSlide = useCallback((index) => {
    setAssistantUndo(null)
    setConfiguration((current) => {
      if (!current || current.slides.length <= 1) return current
      const slides = current.slides.filter((_, slideIndex) => slideIndex !== index)
      setSelectedSlide((selected) => Math.min(selected, slides.length - 1))
      return { ...current, slides }
    })
  }, [])

  const moveSlide = useCallback((index, direction) => {
    setAssistantUndo(null)
    setConfiguration((current) => {
      const moved = current && withMovedItem(current.slides, index, index + direction)
      if (!moved) return current
      setSelectedSlide(moved.selected)
      return { ...current, slides: moved.items }
    })
  }, [])

  const applyAssistantOperations = useCallback(async (
    operations,
    expectedEditorVersion = null,
    ignoreStale = false,
  ) => {
    if (!configurationRef.current || !template) {
      throw new Error(i18n.t('editorV2.core.errors.notReadyForAi'))
    }
    if (
      !ignoreStale
      && expectedEditorVersion != null
      && Number(expectedEditorVersion) !== Number(editorVersionRef.current)
    ) {
      return { stale: true, applied: 0 }
    }

    const snapshot = {
      configuration: cloneValue(configurationRef.current),
      assetUrls: { ...assetUrls },
      selectedSlide,
      selectedItems: { ...selectedItems },
    }
    let nextConfiguration = configurationRef.current
    let nextAssetUrls = { ...assetUrls }
    let nextSelectedSlide = selectedSlide
    const nextSelectedItems = { ...selectedItems }
    const touchedPaths = []

    for (const operation of operations || []) {
      if (operation.op === 'set_field') {
        nextConfiguration = applyEditorFieldValue(
          nextConfiguration,
          operation.path,
          operation.value,
          template,
        )
        touchedPaths.push(operation.path)
        continue
      }
      if (operation.op === 'clear_image') {
        nextConfiguration = setAtPath(nextConfiguration, operation.path, emptyMediaReference())
        touchedPaths.push(operation.path)
        continue
      }
      if (operation.op === 'use_image_candidate') {
        // "Try another photo": one of the run's approved images (already in the asset
        // registry) replaces the slide picture, cropped for the current format.
        const field = assistantImageField(template.schema, operation.path)
        const record = nextConfiguration.assets?.[operation.assetId]
        const sourceUrl = assetSourceUrls[operation.assetId] || record?.url
        const target = field && targetForProfile(template.manifest, field.profile, nextConfiguration)
        if (!field || !sourceUrl || !target) throw new Error(i18n.t('editorV2.core.errors.photoUnavailable'))
        // AD-234: an AI pick the ad never imported (only its published URL, kept in the library)
        // is imported first, as a library click does; the save stores uploads, not URLs.
        let assetId = operation.assetId
        if (!assetSourceUrls[assetId] && !pendingFilesRef.current[assetId] && record?.url) {
          const pendingId = addAsset(await fetchRemoteImage(apiBase, record.url))
          if (!pendingId) throw new Error(i18n.t('editorV2.core.errors.photoUnavailable'))
          const { kind, ...described } = record // eslint-disable-line no-unused-vars
          nextConfiguration = setAtPath(nextConfiguration, `assets.${pendingId}`, {
            ...described, id: pendingId,
            sourceEvidence: { sourceId: assetId, sourceUrl: record.url, transformation: 'import', version: 1 },
          })
          assetId = pendingId
        }
        const media = getAtPath(nextConfiguration, operation.path) || emptyMediaReference()
        if (target.mode !== 'crop') {
          nextConfiguration = setAtPath(nextConfiguration, operation.path, {
            ...emptyMediaReference(), sourceAssetId: assetId, renditionAssetId: assetId,
          })
          nextAssetUrls[assetId] = sourceUrl
          touchedPaths.push(operation.path)
          continue
        }
        const image = await readImageDimensions(sourceUrl)
        const focalPoint = record?.focalPoint || null
        const crop = focalPoint ? cropAroundFocus(image, target, focalPoint) : cropForTarget(null, image, target)
        const rendered = await createCropPreview(sourceUrl, crop, target, focalPoint)
        const renditionAssetId = newPendingRenditionId()
        nextAssetUrls[renditionAssetId] = rendered.previewUrl
        nextConfiguration = setAtPath(nextConfiguration, operation.path, {
          ...media,
          sourceAssetId: assetId,
          renditionAssetId,
          crop: rendered.crop,
          ...(focalPoint ? { focalPoint } : {}),
        })
        if (!focalPoint) {
          const withoutFocus = { ...getAtPath(nextConfiguration, operation.path) }
          delete withoutFocus.focalPoint
          nextConfiguration = setAtPath(nextConfiguration, operation.path, withoutFocus)
        }
        touchedPaths.push(operation.path)
        continue
      }
      if (operation.op === 'focus_image') {
        const field = assistantImageField(template.schema, operation.path)
        const media = getAtPath(nextConfiguration, operation.path)
        const sourceUrl = media?.sourceAssetId && assetSourceUrls[media.sourceAssetId]
        const target = field && targetForProfile(
          template.manifest,
          field.profile,
          nextConfiguration,
        )
        if (!field || !sourceUrl || !target || target.mode !== 'crop') {
          throw new Error(i18n.t('editorV2.core.errors.imageNotFocusable'))
        }
        const image = await readImageDimensions(sourceUrl)
        const crop = cropForFocus(
          image,
          target,
          operation.horizontal,
          operation.vertical,
          operation.zoom,
        )
        const rendered = await createCropPreview(sourceUrl, crop, target)
        const renditionAssetId = newPendingRenditionId()
        nextAssetUrls[renditionAssetId] = rendered.previewUrl
        const manualMedia = { ...media }
        delete manualMedia.focalPoint
        nextConfiguration = setAtPath(nextConfiguration, operation.path, {
          ...manualMedia,
          renditionAssetId,
          crop: rendered.crop,
        })
        touchedPaths.push(operation.path)
        continue
      }

      const collection = editorCollectionDescriptor(template, operation.collectionId)
      if (!collection) throw new Error(i18n.t('editorV2.core.errors.unavailableCollection'))
      const items = [...getAtPath(nextConfiguration, collection.path, [])]
      const identityPath = collection.identityPath || 'id'
      const itemIndex = itemIndexById(items, identityPath, operation.itemId)

      if (operation.op === 'add_collection_item') {
        let item = setAtPath(cloneValue(collection.itemDefaults || {}), identityPath, operation.itemId)
        ;(operation.values || []).forEach((entry) => {
          item = setAtPath(item, entry.path, cloneValue(entry.value))
        })
        items.push(item)
        nextConfiguration = setAtPath(nextConfiguration, collection.path, items)
        if (collection.id === 'slides') nextSelectedSlide = items.length - 1
        else nextSelectedItems[collection.id] = items.length - 1
        touchedPaths.push(collection.path)
        continue
      }
      if (itemIndex < 0) throw new Error(i18n.t('editorV2.core.errors.missingCollectionItem'))
      if (operation.op === 'duplicate_collection_item') {
        const duplicate = setAtPath(
          cloneValue(items[itemIndex]),
          identityPath,
          operation.newItemId,
        )
        items.splice(itemIndex + 1, 0, duplicate)
        nextConfiguration = setAtPath(nextConfiguration, collection.path, items)
        if (collection.id === 'slides') nextSelectedSlide = itemIndex + 1
        else nextSelectedItems[collection.id] = itemIndex + 1
        touchedPaths.push(collection.path)
        continue
      }
      if (operation.op === 'remove_collection_item') {
        items.splice(itemIndex, 1)
        nextConfiguration = setAtPath(nextConfiguration, collection.path, items)
        if (collection.id === 'slides') {
          nextSelectedSlide = Math.min(itemIndex, Math.max(items.length - 1, 0))
        } else {
          nextSelectedItems[collection.id] = Math.min(itemIndex, Math.max(items.length - 1, 0))
        }
        touchedPaths.push(collection.path)
        continue
      }
      if (operation.op === 'move_collection_item') {
        const targetIndex = itemIndex + (operation.direction === 'up' ? -1 : 1)
        const [item] = items.splice(itemIndex, 1)
        items.splice(targetIndex, 0, item)
        nextConfiguration = setAtPath(nextConfiguration, collection.path, items)
        if (collection.id === 'slides') nextSelectedSlide = targetIndex
        else nextSelectedItems[collection.id] = targetIndex
        touchedPaths.push(collection.path)
        continue
      }
      if (operation.op === 'select_collection_item') {
        if (collection.id === 'slides') nextSelectedSlide = itemIndex
        else nextSelectedItems[collection.id] = itemIndex
      }
    }

    setConfiguration(nextConfiguration)
    setAssetUrls(nextAssetUrls)
    setSelectedSlide(nextSelectedSlide)
    setSelectedItems(nextSelectedItems)
    setAssistantUndo({ snapshot, touchedPaths, operationCount: (operations || []).length })
    setFieldErrors((current) => {
      const next = { ...current }
      touchedPaths.forEach((path) => delete next[path])
      return next
    })
    setFormError(null)
    setStatus('idle')
    return { stale: false, applied: (operations || []).length }
  }, [addAsset, apiBase, assetSourceUrls, assetUrls, selectedItems, selectedSlide, template])

  const undoAssistantOperations = useCallback(() => {
    if (!assistantUndo) return false
    setConfiguration(cloneValue(assistantUndo.snapshot.configuration))
    setAssetUrls({ ...assistantUndo.snapshot.assetUrls })
    setSelectedSlide(assistantUndo.snapshot.selectedSlide)
    setSelectedItems({ ...assistantUndo.snapshot.selectedItems })
    setAssistantUndo(null)
    setFormError(null)
    setStatus('idle')
    return true
  }, [assistantUndo])

  // Switching templates starts from the new release's defaults. Between Slider-engine templates
  // (Split Slider, Story, Reel) the ad's content, brand, colours and style carry over
  // (`utils/templateCarry.js`); other switches are a clean restart (the UI warns first). The
  // format survives when the new family offers it — a published ad is locked to its banner
  // format. Campaign Setting metadata is campaign-level, not design, so it carries over
  // (restricted to the new release's formats); `carry.ad` applies the dialog's campaign fields.
  const changeTemplate = useCallback(async (templateReference, carry = null) => {
    setAssistantUndo(null)
    const currentReference = template?.manifest?.releaseKey || template?.manifest?.id
    if (currentReference === templateReference) return
    generationAbortRef.current?.abort()
    generationAbortRef.current = null
    setGenerationJob(null)
    setStatus('loading')
    // `carry.fresh` (Step 1 on a blank new ad): the new template starts from its own defaults —
    // nothing to remember, restore or carry over.
    const fresh = Boolean(carry?.fresh)
    // Remember the ad as it is in the template being left, so switching back restores it.
    const leavingFamily = template?.manifest?.family
    if (!fresh && leavingFamily && configuration) {
      templateSnapshotsRef.current = {
        ...templateSnapshotsRef.current,
        [leavingFamily]: takeSnapshot(template, configuration, assetUrls, assetSourceUrls, assetSrcSets),
      }
      persistTemplateSnapshots(configuration.id, templateSnapshotsRef.current)
    }
    try {
      const nextTemplate = adaptTemplateBundle(
        await loadTemplate(apiBase, templateReference),
      )
      const snapshot = templateSnapshotsRef.current[nextTemplate.manifest.family]
      const restoring = !fresh && Boolean(snapshot?.configuration)
        && snapshot.releaseKey === (nextTemplate.manifest.releaseKey || nextTemplate.manifest.id)
      let next = restoring ? cloneValue(snapshot.configuration) : cloneValue(nextTemplate.defaults)
      if (restoring) {
        // Files that went with a reload (pending uploads) are emptied rather than failing on save.
        next = withoutLostMedia(next, (id) => Boolean(pendingFilesRef.current[id] || assetUrls[id] || assetSourceUrls[id]
          || snapshot.assetUrls?.[id] || snapshot.assetSourceUrls?.[id] || next.assets?.[id]))
        setAssetUrls((current) => ({ ...(snapshot.assetUrls || {}), ...current }))
        setAssetSourceUrls((current) => ({ ...(snapshot.assetSourceUrls || {}), ...current }))
        setAssetSrcSets((current) => ({ ...(snapshot.assetSrcSets || {}), ...current }))
      }
      // A restored template comes back in the format it was left in (a Reel in between is
      // 300×600 only and must not turn the Slider's layout into a half-page one).
      const requestedFormatId = restoring ? getAtPath(next, 'format.id') : getAtPath(configuration, 'format.id')
      const requestedFormat = nextTemplate.manifest.formats.find(
        (entry) => entry.id === requestedFormatId,
      )
      if (requestedFormat) {
        const targetFormat = cloneValue(nextTemplate.defaults.format)
        Object.keys(targetFormat).forEach((key) => {
          if (requestedFormat[key] !== undefined) targetFormat[key] = requestedFormat[key]
        })
        next = setAtPath(next, 'format', targetFormat)
      }
      const format = nextTemplate.manifest.formats.find((entry) => entry.id === next.format.id)
      if (!format) next.format = cloneValue(nextTemplate.defaults.format)
      if (next.schemaVersion === AD_V4) {
        const carried = carry?.campaign || configuration?.campaign || null
        next.campaign = campaignFor({ campaign: carried || defaultCampaign(nextTemplate.manifest, next.format.id) }, nextTemplate.manifest)
        if (!next.campaign.formats.includes(next.format.id)) next.campaign.formats.unshift(next.format.id)
      }
      // Split Slider, Story and Reel share the content engine: the ad moves with the switch.
      let needsCrop = []
      if (!fresh && !restoring && carriesContent(configuration, nextTemplate.defaults)) {
        ({ configuration: next, needsCrop } = carryTemplateContent(configuration, next, nextTemplate))
      }
      Object.entries(carry?.ad || {}).forEach(([key, value]) => {
        if (next.ad && Object.prototype.hasOwnProperty.call(next.ad, key)) next = setAtPath(next, `ad.${key}`, value)
      })
      next = normalizeConditionalOptions(nextTemplate.schema, next)
      const carriedUrls = {}
      const sourceUrlOf = (assetId) => assetSourceUrls[assetId] || assetUrls[assetId]
        || configuration?.assets?.[assetId]?.url || null
      // Crop-mode targets need a crop around the carried focus before the ad validates.
      for (const path of needsCrop) {
        const media = getAtPath(next, path)
        const sourceUrl = sourceUrlOf(media?.sourceAssetId)
        const field = assistantImageField(nextTemplate.schema, path)
        const target = field && targetForProfile(nextTemplate.manifest, field.profile, next)
        try {
          if (!sourceUrl || !target) throw new Error('missing')
          const image = await readImageDimensions(sourceUrl)
          const crop = cropAroundFocus(image, target, media.focalPoint)
          const rendered = await createCropPreview(sourceUrl, crop, target, media.focalPoint)
          const renditionAssetId = newPendingRenditionId()
          carriedUrls[renditionAssetId] = rendered.previewUrl
          next = setAtPath(next, path, { ...media, renditionAssetId, crop: rendered.crop })
        } catch {
          next = setAtPath(next, path, emptyMediaReference())
        }
      }
      // Fit-mode targets show the source itself, so the preview needs the source's URL.
      imageFieldReferences(nextTemplate.schema, next).forEach(({ path }) => {
        const media = getAtPath(next, path)
        const id = media?.renditionAssetId
        if (id && id === media.sourceAssetId && !assetUrls[id] && sourceUrlOf(id)) carriedUrls[id] = sourceUrlOf(id)
      })
      if (Object.keys(carriedUrls).length) setAssetUrls((current) => ({ ...current, ...carriedUrls }))
      setTemplate(nextTemplate)
      setConfiguration(next)
      // A blank new ad in another template is still unedited: its defaults are the clean baseline.
      if (fresh && !adId) setSavedConfiguration(cloneValue(next))
      setFieldErrors({})
      setFormError(null)
      setPreviewUrl(nextTemplate.previewUrl || null)
      setPreviewKey(
        nextTemplate.previewKey
        || nextTemplate.manifest.releaseKey
        || nextTemplate.manifest.id,
      )
      setCompatibility({
        status: 'supported',
        documentVersion: compatibility?.documentVersion || 2,
        currentDocumentVersion: 2,
        legacyDocument: false,
        templateSource: 'central',
        upgradeOptions: [],
      })
      setSelectedSlide(0)
      setSelectedItems(Object.fromEntries(
        (nextTemplate.schema.collections || []).map((collection) => [collection.id, 0]),
      ))
      setStatus('idle')
    } catch (error) {
      setFormError(serverErrors(error).form)
      setStatus('error')
    }
  }, [adId, apiBase, assetSourceUrls, assetSrcSets, assetUrls, compatibility, configuration, template])

  const upgradeTemplate = useCallback(async (templateReference) => {
    setAssistantUndo(null)
    if (!configuration || !template || upgradeBackup) return
    setStatus('loading')
    setFormError(null)
    try {
      const nextTemplate = adaptTemplateBundle(
        await loadTemplate(apiBase, templateReference),
      )
      const sourceRelease = template.manifest.releaseKey || template.manifest.id
      const migrated = migrateConfiguration(configuration, sourceRelease, nextTemplate)
      setUpgradeBackup({
        template,
        configuration: cloneValue(configuration),
        previewUrl,
        previewKey,
        compatibility,
        selectedSlide,
      })
      setTemplate(nextTemplate)
      resetHistory()
      setConfiguration(migrated)
      setPreviewUrl(nextTemplate.previewUrl || null)
      setPreviewKey(
        nextTemplate.previewKey
        || nextTemplate.manifest.releaseKey
        || nextTemplate.manifest.id,
      )
      setCompatibility({
        ...(compatibility || {}),
        status: 'upgrade-preview',
        upgradePreview: {
          from: sourceRelease,
          to: nextTemplate.manifest.releaseKey || nextTemplate.manifest.id,
        },
      })
      setSelectedSlide(Math.min(selectedSlide, Math.max((migrated.slides || []).length - 1, 0)))
      setStatus('idle')
    } catch (error) {
      setFormError(serverErrors(error).form)
      setStatus('error')
    }
  }, [
    apiBase,
    compatibility,
    configuration,
    previewKey,
    previewUrl,
    selectedSlide,
    template,
    upgradeBackup,
  ])

  const cancelUpgrade = useCallback(() => {
    setAssistantUndo(null)
    if (!upgradeBackup) return
    setTemplate(upgradeBackup.template)
    resetHistory()
    setConfiguration(cloneValue(upgradeBackup.configuration))
    setPreviewUrl(upgradeBackup.previewUrl)
    setPreviewKey(upgradeBackup.previewKey)
    setCompatibility(upgradeBackup.compatibility)
    setSelectedSlide(upgradeBackup.selectedSlide)
    setUpgradeBackup(null)
    setFieldErrors({})
    setFormError(null)
    setStatus('idle')
  }, [upgradeBackup])

  const save = useCallback(async () => {
    if (!configuration || !template || ['saving', 'publishing'].includes(status)) return
    const errors = validateConfiguration(localizeSchema(template.schema), configuration, {
      saving: true,
      assetProfiles: template.manifest?.assetProfiles,
    })
    setFieldErrors(errors)
    setFormError(null)
    if (Object.keys(errors).length) {
      setFormError(i18n.t('editorV2.core.errors.reviewFields'))
      return
    }

    setStatus('saving')
    try {
      const serializedConfiguration = JSON.stringify(configuration)
      const referencedPendingFiles = Object.fromEntries(
        Object.entries(pendingFiles).filter(([assetId]) => (
          serializedConfiguration.includes(`"${assetId}"`)
        )),
      )
      const activeHyphenator = hyphenator || await loadHyphenator(configuration?.ad?.language)
      const saved = await saveAd(
        apiBase,
        adId,
        revision,
        hyphenateConfiguration(template.schema, configuration, activeHyphenator),
        referencedPendingFiles,
        designStart?.databaseAdId || null,
        specStart?.key || null,
      )
      setAdId(saved.editReference || saved.id)
      setRevision(saved.revision)
      const cleanSaved = stripSoftHyphens(cloneValue(saved.configuration))
      // The saved ad is the same step, with the uploads stored.
      amendHistory()
      setConfiguration(cleanSaved)
      setSavedConfiguration(cloneValue(cleanSaved))
      setAssetUrls(saved.assetUrls || {})
      setAssetSourceUrls(saved.assetSourceUrls || saved.assetUrls || {})
      setAssetSrcSets(saved.assetSrcSets || {})
      // The other templates' snapshots point at the stored files from now on; uploads only they
      // still use stay pending until one of them is saved.
      const mapping = savedAssetMapping(configuration, saved.configuration)
      const currentFamily = template?.manifest?.family
      const others = Object.fromEntries(Object.entries(templateSnapshotsRef.current)
        .filter(([family]) => family !== currentFamily)
        .map(([family, snapshot]) => {
          const remapped = remapSnapshot(snapshot, mapping, saved.configuration)
          return [family, {
            ...remapped,
            assetUrls: { ...(remapped.assetUrls || {}), ...(saved.assetUrls || {}) },
            assetSourceUrls: { ...(remapped.assetSourceUrls || {}), ...(saved.assetSourceUrls || saved.assetUrls || {}) },
            assetSrcSets: { ...(remapped.assetSrcSets || {}), ...(saved.assetSrcSets || {}) },
          }]
        }))
      templateSnapshotsRef.current = others
      persistTemplateSnapshots(saved.configuration.id, others)
      remapHistory(mapping, saved.configuration)
      // Uploads only an Undo/Redo step still shows stay pending too.
      const stillNeeded = new Set([
        ...Object.values(others).flatMap((snapshot) => [...referencedAssetIds(snapshot.configuration)]),
        ...historyAssetIds(),
      ])
      setPendingFiles((current) => Object.fromEntries(Object.entries(current).filter(([id]) => stillNeeded.has(id))))
      setAssistantUndo(null)
      setUpgradeBackup(null)
      setSavedAt(saved.savedAt)
      if (saved.templateBundle) setTemplate(adaptTemplateBundle(saved.templateBundle))
      setPreviewUrl(saved.previewUrl || previewUrl)
      setPreviewKey(
        saved.previewKey
        || saved.templateBundle?.manifest?.releaseKey
        || previewKey,
      )
      setCompatibility(saved.compatibility || compatibility)
      setPublication(saved.publication || null)
      setFieldErrors({})
      setFormError(null)
      setStatus('saved')
      if (saved.editUrl && (!initialAdId || saved.editReference)) {
        window.history.replaceState({}, '', saved.editUrl)
      }
      return saved
    } catch (error) {
      const normalized = serverErrors(error)
      setFieldErrors(normalized.fields || {})
      setFormError(normalized.form)
      setStatus(normalized.conflict ? 'conflict' : 'error')
    }
  }, [
    adId,
    apiBase,
    assetUrls,
    configuration,
    initialAdId,
    pendingFiles,
    previewKey,
    previewUrl,
    revision,
    status,
    template,
    compatibility,
  , hyphenator])

  // `campaignPatch` records the Step 1 choices (advertiser, clickout) in Campaign Setting
  // metadata as the run starts, so they are saved with the generated ad.
  const generateWithAi = useCallback(async (inputSources = null, campaignPatch = null) => {
    if (!configuration || !template || ['loading', 'saving', 'publishing'].includes(status)) {
      return
    }

    const sourceInputs = {
      ...generationSources(configuration),
      ...(inputSources || {}),
    }

    const templateRelease = template.manifest.releaseKey || template.manifest.id
    if (!templateRelease) {
      setFormError(i18n.t('editorV2.core.errors.templateReleaseMissing'))
      setStatus('error')
      return
    }

    // A different advertiser starts from a clean slate. The merge below keeps
    // whatever the run does not supply, which would otherwise leave the new
    // advertiser's ad showing the previous one's photographs. The cleared
    // configuration is what gets sent as well, so the generator does not read
    // those images back as material the user chose.
    // The template's placeholder brand name ("Your Brand") names no advertiser.
    const placeholderBrand = getAtPath(template.defaults, 'data.brand.name')
    const comparedConfiguration = placeholderBrand && getAtPath(configuration, 'data.brand.name') === placeholderBrand
      ? setAtPath(configuration, 'data.brand.name', '')
      : configuration
    const switched = advertiserChanged(
      generatedForRef.current,
      { advertiser_name: sourceInputs.advertiser_name,
        google_place_id: sourceInputs.google_place_id },
      comparedConfiguration,
    )
    let startingPoint = switched
      ? clearImageSelections(template.schema, configuration)
      : configuration
    if (campaignPatch && startingPoint.schemaVersion === AD_V4) {
      startingPoint = {
        ...startingPoint,
        campaign: { ...campaignFor(startingPoint, template.manifest), ...campaignPatch },
      }
    }
    if (switched) {
      // Drop the previous advertiser's pictures, but not what the ad still shows (a Reel's
      // clip and its poster are the advertiser's own upload and survive the switch).
      const kept = referencedAssetIds(startingPoint)
      const keep = (urls) => Object.fromEntries(Object.entries(urls).filter(([id]) => kept.has(id)))
      setAssetUrls(keep)
      setAssetSourceUrls(keep)
    }
    if (startingPoint !== configuration) setConfiguration(startingPoint)
    generatedForRef.current = {
      name: sourceValue(sourceInputs.advertiser_name),
      placeId: sourceValue(sourceInputs.google_place_id),
    }

    setAssistantUndo(null)
    setFieldErrors({})
    setFormError(null)
    setGenerationJob(null)
    setGenerationRefusal(null)
    setStatus('generating')

    // Cancel stops listening: the agent has no cancel endpoint, so an aborted run simply
    // never reaches the merge below.
    generationAbortRef.current?.abort()
    const controller = new AbortController()
    generationAbortRef.current = controller
    const { signal } = controller
    let jobStarted = false

    try {
      const submitted = await generateAdWithAi(apiBase, {
        adReference: adId,
        templateRelease,
        ad_id: sourceValue(sourceInputs.ad_id) || adId || null,
        advertiser_name: sourceValue(sourceInputs.advertiser_name)
          || configuration?.data?.brand?.name
          || configuration?.brand?.name
          || configuration?.ad?.campaignName
          || 'Advertiser',
        language: sourceValue(sourceInputs.language) || configuration?.ad?.language || 'en',
        briefing_text: sourceValue(sourceInputs.briefing_text),
        website: sourceValue(sourceInputs.website),
        google_place_id: sourceValue(sourceInputs.google_place_id),
        social_post_url: sourceValue(sourceInputs.social_post_url),
        pdf_url: sourceValue(sourceInputs.pdf_url),
        creative_image_url: sourceValue(sourceInputs.creative_image_url),
        briefing_image_url: sourceValue(sourceInputs.briefing_image_url),
        // Clickout "Own Website": the page the ad must link to (the agent takes it over its own pick).
        destination_url: sourceValue(sourceInputs.destination_url),
        source_files: sourceInputs.source_files || [],
        configuration: startingPoint,
      }, signal)

      const generationId = submitted?.generation_id
      if (!generationId) {
        throw new Error(i18n.t('editorV2.core.errors.generationIdMissing'))
      }

      jobStarted = true
      setGenerationJob({
        id: generationId,
        status: submitted?.status || 'queued',
        currentStep: 'queued',
        progress: 0,
        error: null,
        steps: [],
        // The waiting card's clock ("usually ready in about 45 seconds") and what the agent has
        // found so far (site, logo, best photos, brand colours), shown as it arrives.
        startedAt: Date.now(),
        found: null,
      })

      let generated = null
      let lastStatus = submitted?.status || 'queued'
      const maxPolls = 180
      for (let poll = 0; poll < maxPolls; poll += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        if (signal.aborted) return
        const statusData = await fetchGenerationStatus(apiBase, generationId, signal)
        if (signal.aborted) return
        lastStatus = statusData?.status || lastStatus
        const currentStep = statusData?.current_step
        const progress = Number.isFinite(statusData?.progress) ? Number(statusData.progress) : null

        setGenerationJob((current) => ({
          id: generationId,
          // The agent's "completed" means its result is ready to fetch, not that it is in the
          // ad: shown as-is it announced success (banner, toast, chat card) before the result was
          // applied, then fell back to a running state. The editor sets "completed" itself.
          status: lastStatus === 'completed' ? 'applying' : lastStatus,
          currentStep: currentStep || null,
          progress: progress ?? 0,
          error: statusData?.error || null,
          steps: advanceGenerationSteps(current?.steps, currentStep, lastStatus),
          startedAt: current?.startedAt || Date.now(),
          found: statusData?.found || current?.found || null,
        }))

        if (lastStatus === 'completed') {
          generated = await fetchGenerationResult(apiBase, generationId, signal)
          if (signal.aborted) return
          setGenerationJob((current) => ({
            ...(current || {}),
            id: generationId,
            status: 'applying',
            currentStep: 'ad_configuration',
            progress: 100,
            error: null,
            steps: advanceGenerationSteps(current?.steps, current?.currentStep, 'completed'),
          }))
          break
        }

        if (lastStatus === 'failed') {
          const failedResult = await fetchGenerationResult(apiBase, generationId, signal)
          if (signal.aborted) return
          setGenerationJob((current) => ({
            ...(current || {}),
            id: generationId,
            status: 'failed',
            currentStep: 'failed',
            progress: 100,
            error: failedResult?.error || i18n.t('editorV2.core.errors.generationFailed'),
          }))
          throw new Error(failedResult?.error || i18n.t('editorV2.core.errors.generationFailed'))
        }
      }

      if (generated?.generation_status === 'blocked') {
        throw new Error((generated.issues || []).join(' ') || i18n.t('editorV2.core.errors.campaignConflict'))
      }
      if (!generated || !generated.configuration) {
        throw new Error(i18n.t('editorV2.core.errors.generationTimedOut', { status: lastStatus }))
      }
      if (configurationRef.current?.format?.id !== startingPoint?.format?.id
          || configurationRef.current?.template?.releaseKey !== startingPoint?.template?.releaseKey) {
        throw new Error(i18n.t('editorV2.core.errors.generationContextChanged'))
      }
      // Merge through the schema instead of replacing: the generator does not know every
      // template's paths, and a run that returns no logo must not erase the uploaded one.
      let merged = applyGenerationResult({ ...template, schema: localizeSchema(template.schema) }, configurationRef.current, generated)
      if (!switched) merged = withKeptLibrary(configurationRef.current, merged)
      // The website the user named for "Own Website" is where the ad links, whatever the run chose.
      const destination = sourceValue(sourceInputs.destination_url)
      if (destination && merged.ad && Object.prototype.hasOwnProperty.call(merged.ad, 'clickoutUrl')) {
        merged = setAtPath(merged, 'ad.clickoutUrl', destination)
      }
      const next = await importGenerationAssets(template, merged, {
        ownedAssets: savedConfiguration?.assets || {}, pendingFiles: pendingFilesRef.current,
        fetchAsset: (url, abortSignal) => fetchRemoteImage(apiBase, url, abortSignal), addAsset, signal,
        logoImport: generated.asset_import,
      })
      if (signal.aborted) return
      // An empty picture or clip does not make the generated ad invalid. A Reel's clip is the
      // advertiser's upload, and the generator leaves out a picture it could not load and says so
      // in its issues. The editor asks for either one before saving.
      const generatedErrors = validateConfiguration(localizeSchema(template.schema), next, {
        allowEmptyMedia: true,
        assetProfiles: template.manifest?.assetProfiles,
      })
      if (Object.keys(generatedErrors).length) {
        throw new Error(i18n.t('editorV2.core.errors.generatedNeedsCorrection', { details: Object.values(generatedErrors).join(' ') }))
      }
      // A generation starts a new Undo/Redo history.
      resetHistory()
      setConfiguration(next)
      // Switching to another template carries this ad rather than an older snapshot.
      templateSnapshotsRef.current = {}
      persistTemplateSnapshots(next.id, {})
      setSelectedItems({})
      setSelectedSlide(0)
      setGenerationJob((current) => ({
        ...current, status: generated.briefing_version === 'v2' ? 'checking' : 'completed',
        currentStep: 'completed', progress: 100,
        quality: generated.generation_status || 'draft', issues: generated.issues || [],
        generatedConfiguration: next,
      }))
      setFormError(null)
      setStatus('idle')
    } catch (error) {
      if (signal.aborted) return
      const normalized = serverErrors(error)
      setFieldErrors(normalized.fields || {})
      setFormError(normalized.form)
      if (!jobStarted) setGenerationRefusal(normalized.form)
      setGenerationJob((current) => (current
        ? {
          ...current,
          status: 'failed',
          currentStep: 'failed',
          error: normalized.form,
        }
        : current
      ))
      setStatus(normalized.conflict ? 'conflict' : 'error')
    } finally {
      if (generationAbortRef.current === controller) generationAbortRef.current = null
    }
  }, [adId, addAsset, apiBase, configuration, savedConfiguration, status, template])

  const cancelGeneration = useCallback(() => {
    const controller = generationAbortRef.current
    generationAbortRef.current = null
    controller?.abort()
    setGenerationJob(null)
    setGenerationRefusal(null)
    setFormError(null)
    setStatus((current) => (current === 'generating' ? 'idle' : current))
  }, [])

  // Campaign Setting: merges into the editor-only `campaign` metadata (Ad V4 only).
  const updateCampaign = useCallback((patch) => {
    setAssistantUndo(null)
    setConfiguration((current) => {
      if (!current || current.schemaVersion !== AD_V4) return current
      return { ...current, campaign: { ...campaignFor(current, template?.manifest), ...patch } }
    })
    if (status !== 'conflict') {
      setFormError(null)
      setStatus('idle')
    }
  }, [status, template])

  // "Build without AI": the release defaults with every piece of copy and every picture
  // taken out, no logo, and the chosen advertiser's name and website filled in. That state
  // is the clean baseline — nothing has been edited yet, so it is not an unsaved change.
  const startWithoutAi = useCallback(({ advertiser = null, clickoutMode = 'own-website', clickoutUrl = null } = {}) => {
    if (!template || !configurationRef.current || ['loading', 'saving', 'publishing', 'generating'].includes(status)) {
      return
    }
    generationAbortRef.current?.abort()
    generationAbortRef.current = null
    // A fresh manual ad: snapshots of the previous one in other templates no longer apply.
    templateSnapshotsRef.current = {}
    persistTemplateSnapshots(configurationRef.current.id, {})
    // AD-234: the pictures (and the library) stay unless the advertiser changes.
    const switched = advertiserChanged(
      generatedForRef.current,
      { advertiser_name: advertiser?.name, google_place_id: advertiser?.placeId },
      configurationRef.current,
    )
    let next = switched
      ? clearImageSelections(template.schema, cloneValue(configurationRef.current))
      : cloneValue(configurationRef.current)
    for (const collection of template.schema.collections || []) {
      const fields = (collection.itemSections || []).flatMap((section) => section.fields || [])
        .filter((field) => TEXT_FIELD_TYPES.has(field.type))
      const items = getAtPath(next, collection.path, [])
      if (!Array.isArray(items)) continue
      next = setAtPath(next, collection.path, items.map((item) => fields.reduce(
        (row, field) => (typeof getAtPath(row, field.path) === 'string' ? setAtPath(row, field.path, '') : row),
        item,
      )))
    }
    const mode = schemaField(template.schema, 'data.brand.mode')
    if (switched && mode && (mode.options || []).some((option) => option.value === 'none')) {
      next = setAtPath(next, 'data.brand.mode', 'none')
    }
    if (advertiser?.name) {
      if (schemaField(template.schema, 'data.brand.name')) next = setAtPath(next, 'data.brand.name', advertiser.name)
      else if (getAtPath(next, 'brand.name') !== undefined) next = setAtPath(next, 'brand.name', advertiser.name)
    }
    const website = (clickoutMode === 'own-website' && normalizedWebsite(clickoutUrl)) || normalizedWebsite(advertiser?.website)
    if (website && next.ad && Object.prototype.hasOwnProperty.call(next.ad, 'clickoutUrl')) {
      next = setAtPath(next, 'ad.clickoutUrl', website)
    }
    if (next.schemaVersion === AD_V4) {
      next.campaign = { ...campaignFor(next, template.manifest), advertiser, clickoutMode }
    }
    next = normalizeConditionalOptions(template.schema, next)
    generatedForRef.current = advertiser?.name ? { name: advertiser.name, placeId: advertiser.placeId || null } : null
    if (switched) {
      setAssetUrls({})
      setAssetSourceUrls({})
    }
    resetHistory()
    setConfiguration(next)
    setSavedConfiguration(cloneValue(next))
    setGenerationJob(null)
    setAssistantUndo(null)
    setFieldErrors({})
    setFormError(null)
    setSelectedSlide(0)
    setSelectedItems(Object.fromEntries((template.schema.collections || []).map((collection) => [collection.id, 0])))
    setStatus('idle')
  }, [status, template])

  const publish = useCallback(async () => {
    if (
      !adId
      || revision == null
      || dirty
      || ['loading', 'saving', 'publishing'].includes(status)
    ) {
      return
    }

    const databaseAdId = publication?.databaseAdId
    const message = databaseAdId
      ? i18n.t('editorV2.core.publish.replaceConfirm', { adId: databaseAdId, revision })
      : i18n.t('editorV2.core.publish.createConfirm', { revision })
    if (!window.confirm(message)) return

    setStatus('publishing')
    setFormError(null)

    async function perform(force = false) {
      try {
        const published = await publishAd(apiBase, adId, revision, force)
        setAdId(published.editReference || published.id || adId)
        setPublication(published.publication || null)
        setStatus('published')
        if (published.editUrl) {
          window.history.replaceState({}, '', published.editUrl)
        }
      } catch (error) {
        const normalized = serverErrors(error)
        if (
          normalized.code === 'publication_external_change'
          && !force
          && window.confirm(
            `${normalized.form}\n\nReplace the externally changed creative with this saved revision?`,
          )
        ) {
          await perform(true)
          return
        }
        setFieldErrors(normalized.fields || {})
        setFormError(normalized.form)
        setStatus(normalized.conflict ? 'conflict' : 'error')
      }
    }

    await perform(false)
  }, [
    adId,
    apiBase,
    dirty,
    publication,
    revision,
    status,
  ])

  const liveConfiguration = useMemo(
    () => previewConfiguration(
      hyphenateConfiguration(template?.schema, withoutCampaign(configuration), hyphenator),
      { ...assetSourceUrls, ...assetUrls },
      assetSrcSets,
    ),
    [assetSourceUrls, assetSrcSets, assetUrls, configuration, hyphenator, template],
  )

  const onPreviewStatus = useCallback((preview) => {
    if (!preview.checked || preview.configuration !== liveConfiguration) return
    setGenerationJob((job) => {
      if (!job || !['checking', 'completed'].includes(job.status) || !job.generatedConfiguration) return job
      const problems = (preview.diagnostics || []).filter((entry) => entry.severity === 'error')
      const issues = [...new Set([...(job.issues || []), ...problems.map((entry) => entry.message)])]
      const quality = problems.length ? 'draft' : job.quality
      if (job.status === 'completed' && quality === job.quality && issues.length === job.issues.length) return job
      return { ...job, status: 'completed', quality, issues }
    })
  }, [liveConfiguration])

  // The applied result is already in the editor; if the preview never reports back (a hidden or
  // reloading frame), the run still finishes instead of waiting in "checking" for good.
  const checkingJobId = generationJob?.status === 'checking' ? generationJob.id : null
  useEffect(() => {
    if (!checkingJobId) return undefined
    const timer = window.setTimeout(() => {
      setGenerationJob((job) => (job && job.id === checkingJobId && job.status === 'checking'
        ? { ...job, status: 'completed' }
        : job))
    }, 12000)
    return () => window.clearTimeout(timer)
  }, [checkingJobId])

  const previewSelections = useMemo(() => Object.fromEntries(
    (template?.schema?.collections || [])
      .filter((collection) => collection.previewSelection)
      .map((collection) => {
        const items = getAtPath(configuration, collection.path, [])
        const index = selectedItems[collection.id] || 0
        return [collection.path, getAtPath(items[index], collection.identityPath, null)]
      })
      .filter(([, itemId]) => itemId != null),
  ), [configuration, selectedItems, template])

  const selectPreviewItem = useCallback((collectionPath, itemId) => {
    const collection = (template?.schema?.collections || []).find(
      (entry) => entry.path === collectionPath,
    )
    if (!collection) return
    const items = getAtPath(configurationRef.current, collection.path, [])
    const index = items.findIndex(
      (item) => String(getAtPath(item, collection.identityPath, '')) === String(itemId),
    )
    if (index >= 0) setSelectedItem(collection.id, index)
  }, [setSelectedItem, template])

  // AD-256: the screens show the template's labels in the user's language.
  const localizedTemplate = useMemo(
    () => (template ? { ...template, schema: localizeSchema(template.schema) } : template),
    [template, language], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    adId,
    revision,
    templates,
    template: localizedTemplate,
    configuration,
    previewConfiguration: liveConfiguration,
    onPreviewStatus,
    assetUrls,
    assetSourceUrls,
    assetSrcSets,
    pendingFiles,
    selectedSlide,
    selectedItems,
    previewSelections,
    setSelectedSlide,
    setSelectedItem,
    selectPreviewItem,
    status,
    dirty,
    fieldErrors,
    formError,
    savedAt,
    previewUrl,
    previewKey,
    compatibility,
    publication,
    editorVersion,
    assistantUndoAvailable: Boolean(assistantUndo),
    upgradeInProgress: Boolean(upgradeBackup),
    generationJob,
    generationRefusal,
    campaign: configuration?.schemaVersion === AD_V4 ? campaignFor(configuration, template?.manifest) : null,
    updateValue,
    updateCampaign,
    startWithoutAi,
    undo,
    redo,
    canUndo,
    canRedo,
    addAsset,
    addRenditionPreview,
    addSlide,
    removeSlide,
    moveSlide,
    addCollectionItem,
    removeCollectionItem,
    moveCollectionItem,
    reorderCollectionItem,
    duplicateCollectionItem,
    changeTemplate,
    // Whether switching to this release brings back the ad as it was left there.
    hasTemplateSnapshot: (family, releaseKey) => templateSnapshotsRef.current[family]?.releaseKey === releaseKey,
    upgradeTemplate,
    cancelUpgrade,
    save,
    generateWithAi,
    cancelGeneration,
    publish,
    applyAssistantOperations,
    undoAssistantOperations,
  }
}
